import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

export const PAGE_PARAM = "_page";
export const CURSOR_PARAM = "_cursor";

export type PaginationUrlState = {
  currentPage: number;
  currentIndex: number;
  pageTokens: string[];
};

type PaginationStateChange = {
  currentPage: number;
  currentIndex: number;
  pageTokens: string[];
};

type Config<TState extends Record<string, any>> = {
  parseState: (searchParams: URLSearchParams) => TState;
  serializeState: (state: TState) => URLSearchParams;
};

export const normalizePage = (rawPage: string | null) => {
  const parsed = Number(rawPage);
  if (!Number.isFinite(parsed) || parsed <= 1) return 1;
  return Math.floor(parsed);
};

export const isValidPageTokens = (page: number, tokens: string[]) => {
  if (page <= 1) return true;
  return tokens.length === page - 1 && tokens.every((token) => typeof token === "string" && token.trim().length > 0);
};

export const serializeWithPagination = (
  baseParams: URLSearchParams,
  paginationState: { currentPage: number; pageTokens: string[] }
) => {
  const nextParams = new URLSearchParams(baseParams);
  nextParams.delete(PAGE_PARAM);
  nextParams.delete(CURSOR_PARAM);

  if (paginationState.currentPage > 1) {
    nextParams.set(PAGE_PARAM, String(paginationState.currentPage));
    paginationState.pageTokens
      .slice(0, Math.max(0, paginationState.currentPage - 1))
      .forEach((token) => nextParams.append(CURSOR_PARAM, token));
  }

  return nextParams;
};

export const getPaginationStateFromSearchParams = (searchParams: URLSearchParams): PaginationUrlState => {
  const currentPage = normalizePage(searchParams.get(PAGE_PARAM));
  const pageTokens = searchParams.getAll(CURSOR_PARAM);

  if (!isValidPageTokens(currentPage, pageTokens)) {
    return {
      currentPage: 1,
      currentIndex: 0,
      pageTokens: [],
    };
  }

  return {
    currentPage,
    currentIndex: currentPage - 1,
    pageTokens,
  };
};

export const appendSearchToPath = (pathname: string, search: string) => `${pathname}${search || ""}`;

export const usePaginatedTableSearchParams = <TState extends Record<string, any>>({
  parseState,
  serializeState,
}: Config<TState>) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseState(searchParams), [parseState, searchParams]);

  const paginationState = useMemo<PaginationUrlState>(() => {
    return getPaginationStateFromSearchParams(searchParams);
  }, [searchParams]);

  const serializedState = useMemo(() => serializeState(state), [serializeState, state]);

  const syncKey = useMemo(() => {
    const normalized = new URLSearchParams(serializedState);
    normalized.delete(PAGE_PARAM);
    normalized.delete(CURSOR_PARAM);
    return `${location.pathname}?${normalized.toString()}`;
  }, [location.pathname, serializedState]);

  const replaceFilters = useCallback(
    (nextState: TState) => {
      const nextParams = serializeState(nextState);
      setSearchParams(nextParams, { replace: true });
    },
    [serializeState, setSearchParams]
  );

  const replacePagination = useCallback(
    ({ currentPage, pageTokens }: PaginationStateChange) => {
      const nextParams = serializeWithPagination(serializedState, { currentPage, pageTokens });
      const nextSearch = nextParams.toString();
      if (nextSearch === searchParams.toString()) return;
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, serializedState, setSearchParams]
  );

  return {
    state,
    currentPage: paginationState.currentPage,
    currentIndex: paginationState.currentIndex,
    pageTokens: paginationState.pageTokens,
    syncKey,
    replaceFilters,
    replacePagination,
  };
};
